import type { DetailRow } from './DetailTable'
import type { HexItem } from '@/hooks/useHexItems'
import type { WorldFlowHop } from '@/analysis/worldFlowTypes'
import type { SliceMetrics } from '@/analysis/searchContract'
import { resolveCatalogStudio } from '@/data/catalog'

/**
 * Pure chart→table row mappers. Fed by the SAME selectors the charts render
 * from (useHexItems / worldFlowResult hops), so the table shows exactly what
 * the chart shows — same entities, same totals, same severities.
 */

type TickLike = {
  avgSeverity?: unknown
  totalStreams?: unknown
  usersTotal?: unknown
  tickCount?: unknown
  worstMetric?: unknown
  worstMetricSeverity?: unknown
  metrics?: SliceMetrics
  kind?: unknown
}

/** Hex-aligned core metrics — worst/composite must never use fps/buffer/etc. */
const CORE_METRIC_KEYS = [
  'demuxInRate',
  'demuxToDecodeLag',
  'renderRatio',
  'downloadKbps',
  'mediaLatency',
] as const

const CORE_WORST_METRIC_KEYS = new Set([
  'demux_in_rate',
  'demux_to_decode_lag',
  'render_ratio',
  'download_kbps',
  'media_latency',
  'e2e_latency',
])

function sev(v: unknown): 0 | 1 | 2 | null {
  return v === 0 || v === 1 || v === 2 ? v : null
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Worst of the five core headline metric tiers (null tiers ignored). */
function worstFromMetrics(metrics: SliceMetrics | null | undefined): 0 | 1 | 2 | null {
  if (!metrics) return null
  let worst: 0 | 1 | 2 | null = null
  for (const k of CORE_METRIC_KEYS) {
    const t = sev(metrics[k]?.tier)
    if (t == null) continue
    if (worst == null || t < worst) worst = t
  }
  return worst
}

function resolveWorstSeverity(
  tick: TickLike,
  metrics: SliceMetrics | null | undefined,
): 0 | 1 | 2 | null {
  const fromMetrics = worstFromMetrics(metrics)
  const apiWorst = sev(tick.worstMetricSeverity)
  const worstKey = typeof tick.worstMetric === 'string' ? tick.worstMetric : null
  if (worstKey && CORE_WORST_METRIC_KEYS.has(worstKey) && apiWorst != null) {
    return apiWorst
  }
  return fromMetrics ?? sev(tick.avgSeverity)
}

/** `demux_in_rate` → `Demux In Rate` */
export function humanizeMetricKey(raw: string | null | undefined): string | null {
  if (!raw) return null
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

export function hexItemsToDetailRows(
  items: HexItem[],
  onActivate?: (item: HexItem) => void,
): DetailRow[] {
  return items.map((item) => {
    const t = (item.tick ?? {}) as TickLike
    const metrics = t.metrics ?? null
    const avg = sev(t.avgSeverity)
    const worst = resolveWorstSeverity(t, metrics)
    return {
      id: item.id,
      label: item.label,
      severity: avg,
      worstMetricSeverity: worst,
      totalStreams: num(t.totalStreams) ?? (t.kind === 'stream' ? 1 : null),
      usersTotal: num(t.usersTotal),
      tickCount: num(t.tickCount),
      worstMetric: typeof t.worstMetric === 'string' ? t.worstMetric : null,
      metrics,
      onActivate: onActivate ? () => onActivate(item) : undefined,
    }
  })
}

function studioLocation(h: WorldFlowHop): string | null {
  const cat = resolveCatalogStudio(h.value) ?? resolveCatalogStudio(h.label)
  const country = (h.country ?? cat?.country)?.trim()
  if (country) return country.toUpperCase()
  return null
}

export function worldHopsToDetailRows(
  hops: WorldFlowHop[],
  onActivate?: (hop: WorldFlowHop) => void,
): DetailRow[] {
  return hops.map((h) => {
    const metrics = (h.metrics as SliceMetrics | undefined) ?? null
    const avg = sev(h.avgSeverity)
    const worst = resolveWorstSeverity(
      { worstMetric: h.worstMetric, worstMetricSeverity: null, avgSeverity: h.avgSeverity },
      metrics,
    )
    const cat = h.type === 'studio'
      ? (resolveCatalogStudio(h.value) ?? resolveCatalogStudio(h.label))
      : null
    const label = h.type === 'studio'
      ? (cat?.name || h.label || h.value)
      : (h.label || h.value)
    const sub = h.type === 'studio'
      ? studioLocation(h)
      : null
    return {
      id: `${h.type}:${h.value}`,
      label,
      sub,
      severity: avg,
      worstMetricSeverity: worst,
      totalStreams: num(h.totalStreams),
      usersTotal: num(h.usersTotal),
      tickCount: num(h.tickCount),
      worstMetric: h.worstMetric ?? null,
      metrics,
      onActivate: onActivate ? () => onActivate(h) : undefined,
    }
  })
}
